import { CompanyLineItemVisibility } from '@breezy/shared'
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import LoanPromoQualifyNow from '../../../components/Financing/LoanPromo/LoanPromoQualifyNow'
import { MaintenancePlanSavingsBanner } from '../../../components/MaintenancePlanSavings/MaintenancePlanSavingsBanner'
import { PrequalifiedBannerInfo } from '../../../components/PrequalifiedBannerInfo/PrequalifiedBannerInfo'
import { BzSwitch } from '../../../elements/BzSwitch/BzSwitch'
import { HtmlRenderer } from '../../../elements/HtmlRenderer/HtmlRenderer'
import useIsMobile from '../../../hooks/useIsMobile'
import { SectionedCard } from '../../../ui/SectionedCard/SectionedCard'
import { SectionedContent, SectionedSection } from '../../../ui/SectionedCard/SectionedContent'
import { useMessage } from '../../../utils/antd-utils'
import { useQueryParamFlag, useStrictContext } from '../../../utils/react-utils'
import {
  InvoiceContext,
  InvoiceDataContext,
  useInvoiceMaintenancePlanSavingsUpdater,
  useIsInvoiceFinanceable,
} from '../invoiceUtils'
import { InvoiceInfoContent } from './InvoiceInfoContent'
import { LineItemList } from './LineItemList'
import { TotalsSection } from './TotalsSection'

type InvoiceContentProps = {
  footer?: React.ReactNode
  noOuterBorder?: boolean
  customerFacing?: boolean
  lineItemVisibilityStatus: CompanyLineItemVisibility
}

export const InvoiceContent = React.memo<InvoiceContentProps>(
  ({ footer, noOuterBorder, customerFacing, lineItemVisibilityStatus }) => {
    const {
      invoiceGuid,
      accountGuid,
      jobGuid,
      status,
      companyName,
      maintenancePlanSavingsEnabled = false,
      companyMaintenancePlanSavingsEnabled = false,
    } = useStrictContext(InvoiceContext)
    const { messageHtml, lineItems, discounts, invoiceTotals, dynamicPricingType, taxConfiguration } =
      useStrictContext(InvoiceDataContext)
    const [editMode] = useQueryParamFlag('edit')
    const isMobile = useIsMobile()
    const isFinanceable = useIsInvoiceFinanceable(status, invoiceTotals)
    const message = useMessage()
    const [updateMaintenancePlanSavingsEnabled, isUpdatingMaintenancePlanSavings] =
      useInvoiceMaintenancePlanSavingsUpdater()
    const [maintenancePlanSavingsEnabledState, setMaintenancePlanSavingsEnabledState] =
      useState(maintenancePlanSavingsEnabled)

    useEffect(() => {
      setMaintenancePlanSavingsEnabledState(maintenancePlanSavingsEnabled)
    }, [maintenancePlanSavingsEnabled])

    const onToggleMaintenancePlanSavingsEnabled = useCallback(
      async (checked: boolean) => {
        try {
          await updateMaintenancePlanSavingsEnabled(checked)
          setMaintenancePlanSavingsEnabledState(checked)
          message.success(
            checked
              ? 'Maintenance plan savings banner enabled for this invoice.'
              : 'Maintenance plan savings banner disabled for this invoice.',
          )
        } catch (err) {
          console.error('Failed to update maintenance plan savings banner toggle', err)
          message.error('Failed to update maintenance plan savings banner')
        }
      },
      [updateMaintenancePlanSavingsEnabled, message],
    )

    const showToggle = !customerFacing && !editMode && companyMaintenancePlanSavingsEnabled
    const showBanner = maintenancePlanSavingsEnabledState && !editMode

    const sections = useMemo<SectionedSection[]>(() => {
      let sections: SectionedSection[] = [<InvoiceInfoContent />]
      if (messageHtml) {
        sections.push({
          content: (
            <div>
              <div className="text-base font-semibold">Invoice Message</div>
              <div className="mt-2 text-sm">
                <HtmlRenderer htmlContent={messageHtml} />
              </div>
            </div>
          ),
        })
      }
      sections = [
        ...sections,
        ...(lineItemVisibilityStatus === 'HIDDEN' && !customerFacing
          ? [
              {
                verticalPaddingClassName: 'py-0',
                horizontalPaddingClassName: 'px-0',
                flex: 0,
                content: (
                  <div className="flex items-center justify-center gap-2 bg-bz-gray-400 px-4 py-1">
                    <FontAwesomeIcon className="h-4 w-4 text-bz-text-secondary" icon={faEyeSlash} />
                    <div className="text-sm font-semibold leading-[22px] text-bz-text-secondary">
                      Line items are hidden from the customer
                    </div>
                  </div>
                ),
              },
            ]
          : []),
        ...(lineItemVisibilityStatus === 'HIDDEN' && customerFacing
          ? []
          : [
              {
                verticalPaddingClassName: noOuterBorder ? 'pt-4 pb-2' : 'py-1',
                content: lineItems.length ? (
                  <LineItemList
                    viewer={customerFacing ? 'external' : 'internal'}
                    withHeaders={noOuterBorder}
                    lineItems={lineItems}
                    discounts={discounts}
                    dynamicPricingType={
                      // Note: for this we WANT it when customer facing because we want it to scale up the line item numbers
                      customerFacing ? dynamicPricingType : undefined
                    }
                    priceEngineOptions={{
                      taxRoundingStrategy: taxConfiguration.roundingStrategy,
                    }}
                  />
                ) : (
                  <div className="my-6 text-center font-semibold">No Line Items</div>
                ),
              },
            ]),
        {
          content: (
            <div>
              <TotalsSection
                {...invoiceTotals}
                dynamicPricingType={
                  // Note: for this we DON'T want it when customer facing because we don't want it to break out the
                  // markup numbers.
                  customerFacing ? undefined : dynamicPricingType
                }
                discounts={discounts}
                totalLabel={isMobile ? 'Totals' : 'Balance due'}
                showDiscountBasis={!customerFacing} // Only show discount basis for internal views
              />
              {/* Maintenance plan savings controls - internal view only */}
              {showToggle && (
                <div className="mt-4 rounded-lg border border-bz-gray-300 bg-bz-gray-100 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-bz-gray-900">Maintenance plan savings banner</div>
                      <div className="text-xs text-bz-gray-700">
                        Toggle the banner for this invoice without affecting the company default.
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <BzSwitch
                        checked={maintenancePlanSavingsEnabledState}
                        onChange={checked => onToggleMaintenancePlanSavingsEnabled(checked)}
                        loading={isUpdatingMaintenancePlanSavings}
                        disabled={isUpdatingMaintenancePlanSavings}
                        dataTestId="invoice-detail-maintenance-plan-savings-toggle"
                      />
                    </div>
                  </div>
                </div>
              )}
              {showBanner && (
                <div className="mt-4">
                  <MaintenancePlanSavingsBanner
                    invoiceGuid={invoiceGuid}
                    enabled={maintenancePlanSavingsEnabledState}
                    className={customerFacing ? 'w-full' : undefined}
                    unauth={!!customerFacing}
                  />
                </div>
              )}
              {isFinanceable && !editMode && (
                <LoanPromoQualifyNow
                  className="mt-2"
                  type="loan-app"
                  accountGuid={accountGuid}
                  jobGuid={jobGuid}
                  showLoanDisclosure={false}
                  withQualifyNowButton
                  amountUsc={invoiceTotals.dueUsc}
                />
              )}
              {isFinanceable && !editMode && (
                <div className="mt-4">
                  <PrequalifiedBannerInfo accountGuid={accountGuid} companyName={companyName} />
                </div>
              )}
              {footer && <div className="mt-4">{footer}</div>}
            </div>
          ),
        },
      ]
      return sections
    }, [
      invoiceGuid,
      accountGuid,
      companyName,
      customerFacing,
      discounts,
      dynamicPricingType,
      editMode,
      footer,
      invoiceTotals,
      isFinanceable,
      isMobile,
      jobGuid,
      lineItemVisibilityStatus,
      lineItems,
      messageHtml,
      noOuterBorder,
      taxConfiguration,
      maintenancePlanSavingsEnabledState,
      onToggleMaintenancePlanSavingsEnabled,
      isUpdatingMaintenancePlanSavings,
      showToggle,
      showBanner,
    ])
    return noOuterBorder ? <SectionedContent sections={sections} /> : <SectionedCard sections={sections} />
  },
)

InvoiceContent.displayName = 'InvoiceContent'
